// // hooks/usePoolCreation.js
import { useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt, estimateGas, estimateFeesPerGas, getBlock } from 'wagmi/actions';
import { parseEther, formatEther, decodeEventLog, parseGwei } from 'viem';
import { unifarmFactory } from '../../../utils/contract';
import { tokenAmount } from '../../../utils/tokenAmount';

export const usePoolCreation = () => {
    const [isCreatingPool, setIsCreatingPool] = useState(false);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const config = useConfig();

    const estimatePoolCreationGas = useCallback(
        async (owner, rewardTokenAddress, rewardAmount, endDate, tokenDecimals) => {
            try {
                setIsEstimatingGas(true);

                const endEpoch = Math.floor(new Date(endDate).getTime() / 1000);
                const nowEpoch = Math.floor(Date.now() / 1000);

                if (endEpoch <= nowEpoch) {
                    throw new Error("End date must be in the future");
                }

                const rewardAmountWei = tokenAmount(rewardAmount, tokenDecimals)?.parsed;

                // Get latest block to read current base fee
                const block = await getBlock(config);
                const currentBaseFee = block.baseFeePerGas || 0n;

                // Get fee estimates
                const feeData = await estimateFeesPerGas(config);

                // Estimate gas units needed
                const estimatedGas = await estimateGas(config, {
                    address: unifarmFactory.address,
                    abi: unifarmFactory.abi,
                    functionName: 'createUnifarm',
                    args: [owner, rewardTokenAddress, rewardAmountWei, endEpoch, 'ipfs://QmVQrLjqYEVtSXy4AVRtqGifgqyi3T18nHCLvy5tcFeDDv'],
                });

                // Calculate REALISTIC max fee (current base fee + small buffer + priority fee)
                // Instead of MetaMask's 2x multiplier, use 1.1x (10% buffer)
                const bufferMultiplier = 110n; // 110% = 1.1x
                const realisticMaxFeePerGas = (currentBaseFee * bufferMultiplier / 100n) + feeData.maxPriorityFeePerGas;

                // Calculate cost with realistic fees
                const totalGasCostWei = estimatedGas * realisticMaxFeePerGas;
                const totalGasCostETH = tokenAmount(totalGasCostWei, 18)?.formatted;

                return {
                    estimatedGas: estimatedGas?.toString(),
                    currentBaseFee: currentBaseFee?.toString(),
                    maxFeePerGas: realisticMaxFeePerGas?.toString(),
                    maxPriorityFeePerGas: feeData.maxPriorityFeePerGas?.toString(),
                    totalGasCostWei: totalGasCostWei?.toString(),
                    totalGasCostETH: parseFloat(totalGasCostETH),
                    totalGasCostETHFormatted: totalGasCostETH,
                    // Return fee data for transaction
                    feeData: {
                        maxFeePerGas: realisticMaxFeePerGas,
                        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
                    }
                };

            } catch (error) {
                console.error("Error estimating gas:", error);
                throw error;
            } finally {
                setIsEstimatingGas(false);
            }
        },
        [config]
    );

    const createPool = useCallback(
        async (owner, rewardTokenAddress, rewardAmount, endDate, metadataResult, tokenDecimals) => {
            try {
                setIsCreatingPool(true);
                const endEpoch = Math.floor(new Date(endDate).getTime() / 1000);
                const nowEpoch = Math.floor(Date.now() / 1000);

                if (endEpoch <= nowEpoch) {
                    throw new Error("End date must be in the future");
                }

                const rewardAmountWei = tokenAmount(rewardAmount, tokenDecimals)?.parsed;

                // Get latest block for current base fee
                const block = await getBlock(config);
                const currentBaseFee = block.baseFeePerGas || 0n;

                // Get fee estimates
                const feeData = await estimateFeesPerGas(config);

                // Calculate realistic max fee (10% buffer instead of MetaMask's 100% buffer)
                const bufferMultiplier = 110n; // 110% = 1.1x buffer
                const realisticMaxFeePerGas = (currentBaseFee * bufferMultiplier / 100n) + feeData.maxPriorityFeePerGas;

                // CRITICAL: Explicitly set gas parameters to override MetaMask's defaults
                const hash = await writeContract(config, {
                    address: unifarmFactory.address,
                    abi: unifarmFactory.abi,
                    functionName: 'createUnifarm',
                    args: [owner, rewardTokenAddress, rewardAmountWei, endEpoch, metadataResult],
                    maxFeePerGas: realisticMaxFeePerGas,
                    maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
                });

                const receipt = await waitForTransactionReceipt(config, { hash });

                // Extract unifarm address from logs
                let unifarmAddress = null;
                for (const log of receipt?.logs) {
                    try {
                        const decoded = decodeEventLog({
                            abi: unifarmFactory.abi,
                            data: log?.data,
                            topics: log?.topics,
                        });
                        if (decoded?.eventName === "UnifarmCreated") {
                            unifarmAddress = decoded?.args?.unifarm;
                            break;
                        }
                    } catch (err) {
                        // skip logs that don't match
                    }
                }

                return { receipt, hash, unifarmAddress };

            } catch (error) {
                console.error("Error creating pool:", error);
                throw error;
            } finally {
                setIsCreatingPool(false);
            }
        },
        [config]
    );

    return {
        isCreatingPool,
        isEstimatingGas,
        createPool,
        estimatePoolCreationGas
    };
};
// import { useState, useCallback } from 'react';
// import { useConfig } from 'wagmi';
// import { writeContract, waitForTransactionReceipt, estimateGas, getGasPrice } from 'wagmi/actions';
// import { parseEther, formatEther, decodeEventLog } from 'viem';
// import { unifarmFactory } from '../../../utils/contract';
// import { tokenAmount } from '../../../utils/tokenAmount';

// export const usePoolCreation = () => {
//     const [isCreatingPool, setIsCreatingPool] = useState(false);
//     const [isEstimatingGas, setIsEstimatingGas] = useState(false);
//     const config = useConfig();

//     const estimatePoolCreationGas = useCallback(
//         async (owner, rewardTokenAddress, rewardAmount, endDate, tokenDecimals) => {
//             try {
//                 setIsEstimatingGas(true);

//                 const endEpoch = Math.floor(new Date(endDate).getTime() / 1000);
//                 const nowEpoch = Math.floor(Date.now() / 1000);

//                 if (endEpoch <= nowEpoch) {
//                     throw new Error("End date must be in the future");
//                 }

//                 const rewardAmountWei = tokenAmount(rewardAmount, tokenDecimals)?.parsed; //parseEther(rewardAmount?.toString());

//                 // Estimate gas for the transaction
//                 const estimatedGas = await estimateGas(config, {
//                     address: unifarmFactory.address,
//                     abi: unifarmFactory.abi,
//                     functionName: 'createUnifarm',
//                     args: [owner, rewardTokenAddress, rewardAmountWei, endEpoch, 'ipfs://QmVQrLjqYEVtSXy4AVRtqGifgqyi3T18nHCLvy5tcFeDDv'],
//                 });

//                 // Get current gas price
//                 const gasPrice = await getGasPrice(config);

//                 // Calculate total gas cost in wei
//                 const totalGasCost = estimatedGas * gasPrice;

//                 // Convert to ETH
//                 const totalGasCostETH = tokenAmount(totalGasCost, 18)?.formatted //   formatEther(totalGasCost);

//                 return {
//                     estimatedGas: estimatedGas?.toString(),
//                     gasPrice: gasPrice?.toString(),
//                     totalGasCostWei: totalGasCost?.toString(),
//                     totalGasCostETH: parseFloat(totalGasCostETH),
//                     totalGasCostETHFormatted: totalGasCostETH  
//                 };

//             } catch (error) {
//                 console.error("Error estimating gas:", error);
//                 throw error;
//             } finally {
//                 setIsEstimatingGas(false);
//             }
//         },
//         [config]
//     );

//     const createPool = useCallback(
//         async (owner, rewardTokenAddress, rewardAmount, endDate, metadataResult, tokenDecimals) => {
//             try {
//                 setIsCreatingPool(true);
//                 const endEpoch = Math.floor(new Date(endDate).getTime() / 1000);
//                 const nowEpoch = Math.floor(Date.now() / 1000);

//                 if (endEpoch <= nowEpoch) {
//                     throw new Error("End date must be in the future");
//                 }

//                 const rewardAmountWei = tokenAmount(rewardAmount, tokenDecimals)?.parsed;// parseEther(rewardAmount?.toString());

//                 const hash = await writeContract(config, {
//                     address: unifarmFactory.address,
//                     abi: unifarmFactory.abi,
//                     functionName: 'createUnifarm',
//                     args: [owner, rewardTokenAddress, rewardAmountWei, endEpoch, metadataResult],
//                 });

//                 const receipt = await waitForTransactionReceipt(config, { hash });

//                 // Extract unifarm address from logs
//                 let unifarmAddress = null;
//                 for (const log of receipt?.logs) {
//                     try {
//                         const decoded = decodeEventLog({
//                             abi: unifarmFactory.abi,
//                             data: log?.data,
//                             topics: log?.topics,
//                         });
//                         if (decoded?.eventName === "UnifarmCreated") {
//                             unifarmAddress = decoded?.args?.unifarm;
//                             break;
//                         }
//                     } catch (err) {
//                         // skip logs that don't match
//                     }
//                 }
//                 // console.log("New Unifarm Address:", unifarmAddress);

//                 return { receipt, hash, unifarmAddress };

//             } catch (error) {
//                 console.error("Error creating pool:", error);
//                 throw error;
//             } finally {
//                 setIsCreatingPool(false);
//             }
//         },
//         [config]
//     );

//     return {
//         isCreatingPool,
//         isEstimatingGas,
//         createPool,
//         estimatePoolCreationGas
//     };
// };